import type { Rect } from "../types/C1_AppTypes";
import type { FurniturePosition, WallSide } from "../types/C1_ModelTypes";
import { createRoomPlacementGeometry, projectPointToWall, type RoomPlacementGeometry } from "./C1_roomPlacementGeometry";

export const FURNITURE_WALL_DOCK_DISTANCE = 0.5;

export type FurnitureWallPlacement = {
  position: { x: number; y: number };
  side: WallSide;
  wallDocked: "L" | "R" | "M";
  rotation: number;
  reflect: boolean;
  wallId?: string;
};

export type FurnitureWallDocking = {
  position: { x: number; y: number };
  placement?: FurnitureWallPlacement;
};

function furnitureReflectForPlacement(side: WallSide, offset: number, currentReflect: boolean) {
  const orientedOffset = side === "left" || side === "bottom" ? 1 - offset : offset;
  if (orientedOffset < 0.25) return false;
  if (orientedOffset > 0.75) return true;
  return currentReflect;
}

export function pointInFurnitureCoordinates(
  point: { x: number; y: number },
  anchor: { x: number; y: number },
  rotation: number,
  reflect: boolean,
) {
  const angle = (rotation * Math.PI) / 180;
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const rotatedX = dx * Math.cos(angle) + dy * Math.sin(angle);
  return {
    x: reflect ? -rotatedX : rotatedX,
    y: -dx * Math.sin(angle) + dy * Math.cos(angle),
  };
}

export function resolveFurnitureWallDocking(
  point: { x: number; y: number },
  roomRect: Rect | RoomPlacementGeometry,
  options: {
    wallDockable?: boolean;
    autoRotate: boolean;
    autoReflect: boolean;
    disabled?: boolean;
    currentReflect?: boolean;
    excludedSides?: readonly WallSide[];
  },
): FurnitureWallDocking {
  if ((!options.wallDockable && !options.autoRotate && !options.autoReflect) || options.disabled) return { position: point };

  const placement = furnitureWallPlacement(
    point,
    roomRect,
    options.currentReflect,
    options.autoReflect,
    options.excludedSides,
  );
  return { position: placement?.position ?? point, placement };
}

export function furnitureWallPlacement(
  point: { x: number; y: number },
  roomRect: Rect | RoomPlacementGeometry,
  currentReflect = false,
  dockAlongWall = false,
  excludedSides: readonly WallSide[] = [],
): FurnitureWallPlacement | undefined {
  const geometry = "polygon" in roomRect ? roomRect : rectanglePlacementGeometry(roomRect, excludedSides);
  const distances = geometry.walls.filter(wall => wall.visible).map(wall => ({
    wall, ...projectPointToWall(point, wall),
  }));
  if (!distances.length) return undefined;
  const nearest = distances.reduce((current, candidate) =>
    candidate.distance < current.distance ? candidate : current,
  );
  if (nearest.distance > FURNITURE_WALL_DOCK_DISTANCE) return undefined;

  let position = nearest.position;
  const { wall } = nearest;
  const span = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  let directedOffset = nearest.offset;
  if (dockAlongWall) {
    if (directedOffset * span <= FURNITURE_WALL_DOCK_DISTANCE && directedOffset <= 0.5) directedOffset = 0;
    else if ((1 - directedOffset) * span <= FURNITURE_WALL_DOCK_DISTANCE) directedOffset = 1;
    position = { x: wall.start.x + (wall.end.x - wall.start.x) * directedOffset,
      y: wall.start.y + (wall.end.y - wall.start.y) * directedOffset };
  }
  const offset = wall.side === "left" || wall.side === "bottom" ? 1 - directedOffset : directedOffset;
  const wallDocked = offset <= 0.0001 ? "L" : offset >= 0.9999 ? "R" : "M";

  const result: FurnitureWallPlacement = {
    position,
    side: wall.side,
    wallId: wall.wallId,
    wallDocked,
    rotation: (Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * 180 / Math.PI + 360) % 360,
    reflect: furnitureReflectForPlacement(wall.side, Math.min(1, Math.max(0, offset)), currentReflect),
  };

  return result;
}

/** Compatibility for callers that explicitly operate on a rectangular surface. */
function rectanglePlacementGeometry(rect: Rect, excludedSides: readonly WallSide[]): RoomPlacementGeometry {
  const points = [{ x: rect.x1, y: rect.y1 }, { x: rect.x2, y: rect.y1 },
    { x: rect.x2, y: rect.y2 }, { x: rect.x1, y: rect.y2 }];
  return createRoomPlacementGeometry(points.map((start, index) => {
    const side = (["top", "right", "bottom", "left"] as const)[index];
    return { start, end: points[(index + 1) % 4], side, visible: !excludedSides.includes(side), inset: 0 };
  }));
}

export function isFurnitureDockedToWall(relativePosition: FurniturePosition, roomRect: Rect, geometry?: RoomPlacementGeometry): boolean {
  const width = roomRect.x2 - roomRect.x1;
  const height = roomRect.y2 - roomRect.y1;
  const x = roomRect.x1 + relativePosition.x * width;
  const y = roomRect.y1 + relativePosition.y * height;

  return furnitureWallPlacement({ x, y }, geometry ?? roomRect) !== undefined;
}
